<template>
  <component-wrapper
    :class="{
      'product-teasers-fi': true,
      'product-teasers-fi--light': light,
      'product-teasers-fi--white': white,
      'product-teasers-fi--expandable': expandable,
      'product-teasers-fi--content': showContent,
    }"
  >
    <grid-container-fi grid-class="fi-gap-zero md:fi-gap-zero lg:fi-gap-large">
      <div
        v-if="isSlider || currentFields.headline?.value || isEditMode"
        :class="{
          'product-teasers-fi__header': true,
          'product-teasers-fi__header--expandable': expandable,
        }"
      >
        <div
          :class="{
            'product-teasers-fi__products__head': true,
            'product-teasers-fi__products__head--expandable': expandable,
          }"
        >
          <div
            v-if="!isEditMode"
            class="product-teasers-fi__products__headline"
            data-test="product-teasers-headline"
          >
            {{ currentFields.headline?.value }}
          </div>
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="isEditMode"
            class="product-teasers-fi__products__headline"
            v-html="currentFields.headline?.editable"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
        <div
          v-if="isSlider && carouselRef !== null && carouselRef.isActive"
          class="product-teasers-fi__slider-controls"
        >
          <carousel-slider-controls-fi
            :carousel="carouselRef"
            :dark="white"
          />
        </div>
      </div>

      <div
        :class="{
          'product-teasers-fi__products': true,
          'product-teasers-fi__products--static': !expandable,
          'product-teasers-fi__products--expandable': expandable,
        }"
      >
        <div class="product-teasers-fi__products-wrapper">
          <carousel-fi
            v-if="catalogData?.length"
            :key="changesCount"
            ref="carouselRef"
            v-intersect.once="onIntersect"
            :class="{
              'product-teasers-fi__slider': true,
              'product-teasers-fi__slider--no-slider': !isSlider,
              'product-teasers-fi__slider--with-content': showContent,
            }"
            :settings="sliderSettings"
            theme="product-teasers"
            no-arrows
            clipped-right
          >
            <carousel-slide-fi
              v-for="(item, index) in catalogData"
              :key="item.id"
              theme="product-teasers"
              :class="{
                'product-teasers-fi__slide': true,
                'product-teasers-fi__slide--expandable': expandable,
              }"
            >
              <div class="product-teasers-fi__slide-wrapper">
                <product-tile-fi
                  v-if="isVariant"
                  :product="item as unknown as TileProduct"
                  :best-price="bestPrices[item.id]"
                  :best-price-loading="loadingPrices"
                  :out-of-stock="retailerOutOfStock[index]"
                  :is-recommendation="isRecommendation"
                  :is-guided-recommandation="isGuidedRecommandation"
                  :expandable
                  @product-added="$emit('product-added', $event)"
                  @tile-clicked="
                    trackSelectItem(
                      item as unknown as TrackingProductOrCategory,
                      !isStandaloneVariant,
                    )
                  "
                />
                <product-category-tile-fi
                  v-else
                  :category="item"
                  :is-guided-recommandation="isGuidedRecommandation"
                  @tile-clicked="trackSelectItem(item as unknown as TrackingProductOrCategory)"
                />
              </div>
            </carousel-slide-fi>
            <carousel-slide-fi
              v-if="expandable"
              theme="product-teasers"
              class="product-teasers-fi__slide product-teasers-fi__slide--expandable"
            >
              <div><!-- spacer --></div>
            </carousel-slide-fi>
          </carousel-fi>
        </div>
      </div>
      <div
        v-if="showContent"
        class="product-teasers-fi__content"
      >
        <div class="product-teasers-fi__content-container">
          <richtext-fi
            v-if="currentFields.content"
            class="product-teasers-fi__text"
            is-theme-light
            :html-content="currentFields.content"
          />
        </div>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { CarouselFi, CarouselSlideFi, CarouselSliderControlsFi, RichtextFi } from 'atoms';
import {
  computed,
  onMounted,
  onServerPrefetch,
  ref,
  useTemplateRef,
  watch,
  watchEffect,
  watchPostEffect,
} from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import ProductCategoryTileFi from '@/components/product-category/product-category-tile/ProductCategoryTileFi.vue';
import ProductTileFi, {
  type AddProductData,
  type TileProduct,
} from '@/components/product-tile-fi/ProductTileFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useBestPrices } from '@/composables/multiBestPrice';
import { type TrackingProductOrCategory, useTracking } from '@/composables/tracking';
import type {
  GetProductCategoryListByIdQuery,
  GetProductCategoryListByIdQueryVariables,
  GetProductListByIdQuery,
  GetProductListByIdQueryVariables,
  GetRetailerAvailabilityForIdsQuery,
  GetRetailerAvailabilityForIdsQueryVariables,
} from '@/generated/graphql';
import GETPRODUCTCATEGORYLISTBYID from '@/graphql/getProductCategoryListById.gql';
import GETPRODUCTLISTBYID from '@/graphql/getProductListById.gql';
import GET_RETAILER_AVAILABILITY_FOR_IDS from '@/graphql/getRetailerAvailabilityForIds.gql';
import type { CheckboxField, RichTextField, SingleLineTextField } from '@/sitecoreFieldTypes';
import { useNotificationStore } from '@/store/pinia/notification';
import screenWidths from '@/tailwind/screenWidths';

interface Fields {
  familyList?: SingleLineTextField;
  productList?: SingleLineTextField;
  hideContent?: CheckboxField;
  content?: RichTextField;
  headline?: SingleLineTextField;
}

defineOptions({
  name: 'ProductTeasersFi',
});

const fields = useFields('ProductTeasersFiFields');

const props = withDefaults(
  defineProps<{
    customFields?: Fields;
    /**
     * already resolved catalog data. When set, the teasers render straight from
     * it instead of looking the products up again.
     */
    products?: CatalogItem[];
    light?: boolean;
    white?: boolean;
    isRecommendation?: boolean;
    isGuidedRecommandation?: boolean;
    showVariantsCount?: boolean;
    expandable?: boolean;
  }>(),
  {
    customFields: () => ({}),
    products: undefined,
  },
);

defineEmits<{
  'product-added': [value: AddProductData];
}>();

const { isXL, isLG, isMD, isSM, windowWidth } = useBreakpoints();
const { trackViewItemList, trackSelectItem } = useTracking();
const fischer = useFischer();
const notificationStore = useNotificationStore();

const changesCount = ref(0);
export type CatalogItem = NonNullable<
  | NonNullable<GetProductCategoryListByIdQuery['getProductCategoryListById']>[number]
  | NonNullable<GetProductListByIdQuery['getProductListById']>[number]
>;

const catalogData = ref<CatalogItem[] | null>(null);
const isMounted = ref(false);
const carouselRef = useTemplateRef<any>('carouselRef');

const isStandaloneVariant = computed<boolean>(() => Object.keys(props.customFields).length === 0);

const currentFields = computed<Fields>(
  () => (isStandaloneVariant.value ? fields : props.customFields) || {},
);

const isVariant = computed<boolean>(() =>
  Boolean(props.products || currentFields.value.productList),
);

const productIds = computed<string[]>(() =>
  props.products
    ? props.products.map(({ id }) => id)
    : currentFields.value.productList?.value?.split('|') || [],
);

const familyIds = computed<string[]>(
  () =>
    (currentFields.value.familyList?.value && currentFields.value.familyList.value.split('|')) ||
    [],
);

const showContent = computed<boolean>(() => {
  if (props.isRecommendation || currentFields.value.hideContent?.value) {
    return false;
  }

  return Boolean(currentFields.value.content?.value) || fischer.app.isEditMode();
});

const sliderSettings = computed(() => ({
  rewind: false,
  pagination: false,
  perPage: 1,
  perMove: 1,
  destroy: false,
  gap: props.expandable ? 12 : 24,
  noDrag: 'input, .no-drag',
  autoWidth: props.expandable,
  breakpoints: props.expandable
    ? {
        [screenWidths.sm]: {
          gap: 24,
        },
      }
    : {
        [screenWidths.xs]: {
          perPage: 1,
        },
        [screenWidths.sm]: {
          perPage: 2,
        },
        [screenWidths.md]: {
          perPage: 3,
        },
        [screenWidths.lg]: {
          perPage: showContent.value ? 2 : 4,
        },
        [screenWidths.xl]: {
          perPage: showContent.value ? 3 : 5,
        },
      },
}));

const visibleSlidesCount = computed<number>(() => {
  if (isXL.value) {
    return showContent.value || props.expandable ? 3 : 5;
  }
  if (isLG.value) {
    return showContent.value || props.expandable ? 2 : 4;
  }
  if (isMD.value) {
    return props.expandable ? 2 : 3;
  }
  if (isSM.value) {
    return props.expandable ? 1 : 2;
  }
  return 1;
});

const isExpandableScrollable = ref(false);

watchPostEffect(() => {
  // rerun on every resize
  void windowWidth.value; // NOSONAR

  if (!props.expandable || !carouselRef.value) {
    isExpandableScrollable.value = false;
    return;
  }

  void carouselRef.value.isActive; // NOSONAR

  const el = carouselRef.value.$el as HTMLElement | null;
  const list = el?.querySelector<HTMLElement>('.splide__list');
  const track = el?.querySelector<HTMLElement>('.splide__track');

  const spacerSlide = el?.querySelector<HTMLElement>('.splide__slide:last-child');
  const spacerWidth = spacerSlide?.offsetWidth ?? 0;

  isExpandableScrollable.value = !!(
    list &&
    track &&
    list.scrollWidth - spacerWidth > track.clientWidth
  );
});

const isSlider = computed<boolean>(() => {
  if (!isMounted.value) {
    return false;
  }

  if (props.expandable) {
    return isExpandableScrollable.value;
  }

  return Boolean(catalogData.value?.length && catalogData.value?.length > visibleSlidesCount.value);
});

const retailerOutOfStock = computed<Record<number, boolean>>(() =>
  (getRetailerAvailabilityForIdsData.value?.getRetailerAvailabilityForIds || []).map(
    (availability) => {
      if (!availability) {
        // something went wrong, default to 'can search for retailers'
        return false;
      }
      return availability.stock === 'empty' || availability.stock === null;
    },
  ),
);

watch(catalogData, () => {
  changesCount.value += 1;
});

watch(
  () => props.products,
  (products) => {
    if (products) {
      catalogData.value = products;
    }
  },
  { immediate: true },
);

const onQueryError = (...errorValue: any) => {
  console.error(...errorValue);
  notificationStore.openGeneralErrorNotification();
};

onMounted(() => {
  isMounted.value = true;
});

const loadPrices = computed<boolean>(() =>
  Boolean(
    productIds.value?.length &&
    fischer.shop.isClosedShop() &&
    !fischer.app.isFite() &&
    !fischer.app.isEditMode(),
  ),
);

// R1: product category list query — SSR-viable content, needs R0
const {
  data: productCategoryListData,
  error: productCategoryListError,
  executeQuery: executeCategoryListQuery,
} = useQuery<GetProductCategoryListByIdQuery, GetProductCategoryListByIdQueryVariables>({
  query: GETPRODUCTCATEGORYLISTBYID,
  variables: computed(() => ({
    language: fischer.catalogLocale(),
    ids: familyIds.value.join(','),
    channel: fischer.brandId(),
  })),
  pause: computed(() => familyIds.value.length === 0),
});

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: restored SSR results are delivered synchronously during setup, so a lazy watcher
// would never see them and catalogData would stay empty after hydration.
watch(
  productCategoryListData,
  (data) => {
    if (data?.getProductCategoryListById) {
      catalogData.value = data.getProductCategoryListById.filter(Boolean) as CatalogItem[];
    }
  },
  { immediate: true, flush: 'sync' },
);

watch(productCategoryListError, (error) => {
  if (error) onQueryError(error);
});

onServerPrefetch(async () => {
  if (familyIds.value.length === 0) return;
  await executeCategoryListQuery();
});

// R1: product list query — contains accessToken/userId (user-personalized), skip R0
const { data: productListData, error: productListError } = useQuery<
  GetProductListByIdQuery,
  GetProductListByIdQueryVariables
>({
  query: GETPRODUCTLISTBYID,
  variables: computed<GetProductListByIdQueryVariables>(() => ({
    baseSiteId: fischer.baseSiteId(),
    channel: fischer.brandId(),
    language: fischer.catalogLocale(),
    accessToken: fischer.user.accessToken(),
    userId: fischer.user.id(),
    ids: productIds.value.join(','),
  })),
  pause: computed(() => Boolean(props.products) || productIds.value.length === 0),
});

watch(productListData, (data) => {
  if (data?.getProductListById) {
    catalogData.value = data.getProductListById.filter(Boolean) as CatalogItem[];
  }
});

watch(productListError, (error) => {
  if (error) onQueryError(error);
});

const { bestPrices, loadingPrices } = useBestPrices(productIds, { enabled: loadPrices });

// R1: retailer availability query — gated by !IS_SSR, skip R0
const { data: getRetailerAvailabilityForIdsData, error: retailerAvailabilityError } = useQuery<
  GetRetailerAvailabilityForIdsQuery,
  GetRetailerAvailabilityForIdsQueryVariables
>({
  query: GET_RETAILER_AVAILABILITY_FOR_IDS,
  variables: computed<GetRetailerAvailabilityForIdsQueryVariables>(() => ({
    baseSiteId: fischer.baseSiteId(),
    productIds: productIds.value,
    useGtins: fischer.useGtins(),
  })),
  pause: computed(
    () => IS_SSR || !fischer.shop.isRetailConnectCart() || productIds.value.length === 0,
  ),
});

watch(retailerAvailabilityError, (error) => {
  if (error) onQueryError(error);
});

const isIntersected = ref(false);
const onIntersect = () => {
  isIntersected.value = true;
};
watchEffect(() => {
  if (isIntersected.value && catalogData.value?.length) {
    trackViewItemList(catalogData.value as unknown as TrackingProductOrCategory[]);
  }
});
</script>

<style scoped src="./product-teasers-fi" />
